import { KPI_GROUPS, type KpiGroupId } from "./kpi-groups";

/**
 * Arrangement of the five KPI sections.
 *
 * `order` and `removed` are saved on the server. `collapsed` stays in this
 * browser.
 */
export interface KpiSectionLayout {
  order: KpiGroupId[];
  collapsed: KpiGroupId[];
  removed: KpiGroupId[];
}

const KPI_GROUP_IDS = new Set<string>(KPI_GROUPS.map((group) => group.id));

export function isKpiGroupId(value: unknown): value is KpiGroupId {
  return typeof value === "string" && KPI_GROUP_IDS.has(value);
}

export function defaultKpiSectionLayout(): KpiSectionLayout {
  return {
    order: KPI_GROUPS.map((group) => group.id),
    collapsed: [],
    removed: [],
  };
}

export function kpiSectionCollapsedKey(tenantId: string, userId: string): string {
  return `nexus.kpi-section-collapsed:${tenantId}:${userId}`;
}

/** Known ids, first occurrence wins. Does not append missing sections. */
export function uniqueKpiSectionIds(value: unknown): KpiGroupId[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<KpiGroupId>();
  const next: KpiGroupId[] = [];
  for (const id of value) {
    if (!isKpiGroupId(id) || seen.has(id)) continue;
    seen.add(id);
    next.push(id);
  }
  return next;
}

export function readCollapsedSections(raw: string | null): KpiGroupId[] {
  if (!raw) return [];
  try {
    return uniqueKpiSectionIds(JSON.parse(raw));
  } catch {
    return [];
  }
}

/** Known ids, first occurrence wins, then any missing section in the default order. */
export function normalizeKpiSectionOrder(order: readonly KpiGroupId[] | undefined): KpiGroupId[] {
  const seen = new Set<KpiGroupId>();
  const next: KpiGroupId[] = [];
  for (const id of order ?? []) {
    if (!isKpiGroupId(id) || seen.has(id)) continue;
    seen.add(id);
    next.push(id);
  }
  for (const group of KPI_GROUPS) {
    if (!seen.has(group.id)) next.push(group.id);
  }
  return next;
}

/**
 * Swap `id` with its neighbour in `visibleIds` (the sections actually on screen).
 * Hidden sections stay where they are, so a restored section comes back at its index.
 * Moving the first section up, or the last down, leaves the order unchanged.
 */
export function moveKpiSection(
  order: readonly KpiGroupId[],
  id: KpiGroupId,
  direction: "up" | "down",
  visibleIds?: readonly KpiGroupId[],
): KpiGroupId[] {
  const normalized = normalizeKpiSectionOrder(order);
  const visible = (visibleIds ?? normalized).filter((candidate) => normalized.includes(candidate));
  const index = visible.indexOf(id);
  const neighbour = direction === "up" ? visible[index - 1] : visible[index + 1];
  if (index === -1 || !neighbour) return normalized;

  const next = [...normalized];
  const from = next.indexOf(id);
  const to = next.indexOf(neighbour);
  next[from] = neighbour;
  next[to] = id;
  return next;
}

export function toggleKpiSectionCollapsed(
  layout: KpiSectionLayout,
  id: KpiGroupId,
): KpiSectionLayout {
  if (layout.removed.includes(id)) return layout;
  const collapsed = layout.collapsed.includes(id)
    ? layout.collapsed.filter((candidate) => candidate !== id)
    : [...layout.collapsed, id];
  return { ...layout, collapsed };
}

/** Hide the section. A removed section is expanded, so restoring it shows its boxes. */
export function removeKpiSection(layout: KpiSectionLayout, id: KpiGroupId): KpiSectionLayout {
  if (layout.removed.includes(id)) return layout;
  return {
    ...layout,
    collapsed: layout.collapsed.filter((candidate) => candidate !== id),
    removed: [...layout.removed, id],
  };
}

export function restoreKpiSections(layout: KpiSectionLayout): KpiSectionLayout {
  if (layout.removed.length === 0) return layout;
  return { ...layout, removed: [] };
}
